import React from 'react'
import { Button as MuiButton, CircularProgress, alpha, useTheme } from '@mui/material'
import type { ButtonProps as MuiButtonProps, Theme } from '@mui/material'
import { authPx } from '../authScale'

export interface AuthPrimaryButtonProps extends Omit<MuiButtonProps, 'variant'> {
  loading?: boolean
}

/**
 * The base gradient, exported so a caller that needs to override a nested
 * block wholesale (e.g. the OTP submit button's bespoke `&:disabled` below)
 * can reference the same ramp instead of retyping it — keeping this the only
 * file in the auth page family that spells the gradient out.
 *
 * MODE-AWARE, which reverses the previous rule and is worth recording. The
 * gradient version ran brand[950] → brand[700] in both modes deliberately, on
 * the argument that a brand CTA is the product's own colour rather than a
 * surface and so should look identical everywhere. That argument fell to a
 * stronger one: the CTA and the ambient background were the same hue, so the
 * single element that must be unmissable was competing with the atmosphere
 * behind it.
 *
 * The label follows the same reversal. It used to be pinned to `common.white`
 * because it was contrast against the PURPLE rather than against the page; now
 * the button IS the page inverted, so the label is `background.default` and
 * flips with it.
 */
/**
 * NEUTRAL, not the brand gradient: near-black on light, near-white on dark.
 *
 * The purple gradient competed with the ambience for the same HUE. Every other
 * element on these pages is neutral, so the one thing that has to be unmissable
 * was the same colour as the atmosphere behind it — worst in light mode, where
 * a purple button sat on a lavender-tinted page. A neutral separates from the
 * backdrop completely in both modes, and it lets the purple stay purely
 * atmospheric instead of being spent twice.
 *
 * The name is kept (`authPrimaryGradient`) because callers import it to
 * override nested elements; the value is a flat colour now, and a `background`
 * accepts either.
 *
 * `text.primary` / `background.paper` rather than literal black and white, so
 * it tracks the theme's own near-black (#09090b-ish) and near-white instead of
 * introducing a third pair of absolutes.
 */
export const authPrimaryGradient = (theme: Theme) =>
  // 95%, not `text.primary` at full strength. That token is #fafafa in dark
  // (98%), which is correct for TEXT — a few glyphs — and too much light at
  // full-bleed button size against a near-black page. 95% takes the glare off
  // without drifting into grey. Composited over the page rather than a fixed
  // hex, so it lands correctly in both modes.
  alpha(theme.palette.text.primary, 0.95)

const authPrimaryGradientHover = (theme: Theme) =>
  // Lifts AWAY from the page on hover in both modes: lighter on light (the
  // button is dark), darker on dark (the button is light).
  theme.palette.mode === 'light'
    ? alpha(theme.palette.text.primary, 0.82)
    : alpha(theme.palette.text.primary, 0.88)

/**
 * The one gradient primary CTA for the sign-in / sign-up / password-setup
 * page family (VERA-1095 — house rule: extract a shared component on the
 * second hand-roll, working-conventions §36).
 *
 * Before this, the same gradient + hover + disabled treatment was hand-rolled
 * on a raw MUI `Button` in THREE places: `SignInPage` (3 buttons — OTP
 * submit, password sign-in, "Receive code"), `PasswordSetup` (1 button), and
 * `SignUpPage`, which at least centralised its OWN copy into a local
 * `GRADIENT_CTA_SX` const but still hand-rolled the literal independently of
 * the other two files. That's 8 separate copies of the gradient literal
 * across the family — any future tweak (a hover shade, a radius change) had
 * to be made in up to five places, and it had already drifted once
 * (`SignUpPage`'s disabled state briefly fell back to MUI's default grey
 * before being hand-matched back to sign-in's).
 *
 * Deliberately NOT built on the shared `@repo/ui`-backed `Button`: this is a
 * gradient CTA, and the design system's `Button` has no gradient variant —
 * `SignUpPage` previously imported it for this CTA only to override nearly
 * every one of its style decisions via `sx` (including its OWN disabled-state
 * opacity/colour, which `@repo/ui`'s `Button` computes from the theme). It
 * now renders this component like every other page in the family does.
 *
 * LOADING BEHAVIOUR — the spinner REPLACES the label, it does not render
 * alongside it. This is sign-in's existing behaviour on all three of its
 * hand-rolled buttons (`isSubmitting ? <CircularProgress/> : 'Submit'`),
 * kept here as the canonical treatment rather than the `@repo/ui` `Button`'s
 * spinner+label pattern `SignUpPage` used to inherit. Two reasons: (1) it's
 * what 3 of the 4 hand-rolled instances already did — the majority
 * behaviour, not a new one; (2) with a short one/two-word label
 * ("Submit", "Sign in", "Continue to checkout") replacing it keeps the
 * button's content centred and its apparent width stable, rather than
 * shifting when a spinner is inserted before the text.
 *
 * `sx` is merged AFTER the base styles (same pattern as `@repo/ui`'s own
 * `Button`: `sx={{ ...base, ...sx }}`), so a caller can layer extras (the
 * checkout CTA's purple-pulse animation) or fully override a nested block
 * (the OTP submit button's bespoke `&:disabled` — dimmed TEXT at full
 * opacity, rather than every other instance's dimmed WHOLE BUTTON at 50%
 * opacity, preserved from the pre-extraction behaviour) by naming that same
 * top-level key.
 */
export const AuthPrimaryButton = ({
  loading = false,
  disabled,
  children,
  sx,
  ...rest
}: AuthPrimaryButtonProps) => {
  const theme = useTheme()

  return (
    <MuiButton
      disabled={disabled || loading}
      sx={{
        // Lengths follow `--auth-scale` (authScale.ts); unset, these are the px.
        height: authPx(48),
        minHeight: authPx(40),
        borderRadius: authPx(8),
        background: authPrimaryGradient(theme),
        // No glow. A coloured shadow under a neutral button reads as a stray
        // purple halo rather than as lift, and the button already separates
        // from the page by being its inverse.
        color: theme.palette.background.default,
        fontSize: authPx(16),
        // 700, from vera.inc's /enter "Continue" (Spencer, 2026-09-23: "I also
        // like the more bolded sign in font"). Every auth primary CTA is this
        // component, so sign-in, sign-up and password setup move together.
        fontWeight: 700,
        textTransform: 'none',
        '&:hover': {
          background: authPrimaryGradientHover(theme),
        },
        // VRG-460: PRESS FEEDBACK. Asked for on sign-in's "Receive code", but
        // added HERE rather than at that one call site, because this component
        // is the single primary CTA for the whole sign-in / sign-up /
        // password-setup family (working-conventions §47 — improve the shared
        // component rather than build around it). A press that feels different
        // on sign-in than on sign-up is the drift this component was extracted
        // to stop.
        //
        // HONEST NOTE ON THE SPEC: the ask was "like the sign up pages do
        // presently", and sign-up had no bespoke press treatment either — both
        // pages relied on MUI's default ripple, which on a full-bleed neutral
        // slab is nearly invisible. So this ADDS a shared behaviour to both
        // rather than porting one that already existed. Stated because the
        // difference matters if the intent was strictly "make sign-in match".
        //
        // Deliberately quiet: the fill steps to its hover shade and the button
        // takes a ~1.5% scale-down (about 6px across a 396px column). Enough to
        // register as a press at the moment of contact, not enough to read as
        // the button moving. Fast (120ms) because press feedback that outlasts
        // the press stops being feedback.
        transition: 'background 120ms ease, transform 120ms ease',
        '&:active': {
          background: authPrimaryGradientHover(theme),
          transform: 'scale(0.985)',
        },
        // The scale is motion; the fill change is not. Reduced-motion keeps the
        // colour step so the press is still acknowledged, and drops only the
        // movement.
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'background 120ms ease',
          '&:active': { transform: 'none' },
        },
        // LOADING and DISABLED both set the `disabled` attribute — the button must
        // not be clickable in either — but they must not LOOK the same, and they did.
        //
        // The muted-fill block below is right for "you cannot press this yet" and
        // wrong for "I am working on what you just pressed". While loading it made a
        // pressed button fade to a dim wash, and the spinner is `background.default`
        // (near-black on dark), so a near-black spinner sat on a near-black wash and
        // the whole control read as an empty outlined box that had broken.
        //
        // Loading keeps the PRIMARY fill. The button the user just pressed should
        // still look like the button they pressed; only its label swaps for a
        // spinner, which then has the full-contrast surface it was designed against.
        // DISABLED KEEPS THE PRIMARY FILL — Spencer's call, and it reverses the
        // muted treatment that was here.
        //
        // The history is worth keeping because the muted version was itself a
        // fix. It went: 50% opacity (muddy grey, illegible) -> a 12% fill with
        // a `text.disabled` label (measured 2.60:1, under the 3:1 floor, so the
        // word naming the unavailable action was the least legible thing on the
        // page) -> `text.secondary` on that fill (5.11:1, legible) -> this.
        //
        // Each step made the muted state better and none of them made it read
        // as a BUTTON. On these auth pages the control is the page: sign-in is
        // one field and one button, and a washed-out slab on arrival makes the
        // only affordance look broken rather than pending.
        //
        // KNOWN TRADE, stated rather than hidden: it now looks pressable while
        // it is not. `disabled` is still set, so it cannot submit and it is
        // still announced as disabled — the loss is purely that a sighted user
        // gets no advance warning, and finds out by clicking. That is the cost
        // of the instruction and it is a real one; if it bites, the fix is to
        // drop `disabled` entirely and validate on click so the press produces
        // an actual message, NOT to reintroduce a wash.
        '&:disabled': {
          background: authPrimaryGradient(theme),
          /*
            DIMMED INK, AND IT MUST BE `background.default` — NOT WHITE.

            The fill is `alpha(text.primary, 0.95)`, which INVERTS with the mode: near-white
            in dark, near-black in light. So the label has to be the theme's opposite end,
            which `background.default` is by definition. Two call sites (SignInPage's
            "Submit", TwoFactorChallenge's "Verify") used to override this with
            `alpha(common.white, 0.52)` — correct-looking in light mode, and in DARK mode
            white text at 52% on a near-white button. Reported as unreadable, and it was:
            those overrides are deleted, this is the one source.

            0.55 rather than full strength because the call sites were right that a
            disabled button needs to LOOK disabled — they were dimming the TEXT at full
            button opacity, which is not the "wash" the note above rejects (that argument is
            about fading the whole slab so it stops reading as a button). Measured on the
            dark page: ink composites to ~#6f6f6f on the ~#eeeeee fill, about 3.4:1 — muted,
            still legible, and in line with the shared Button's own disabled target (~3.3:1).
          */
          color: alpha(theme.palette.background.default, 0.55),
          opacity: 1,
          // `loading` also sets `disabled`, so this is what stops a button from
          // sitting in its pressed geometry for the duration of the request the
          // press kicked off.
          transform: 'none',
        },
        ...sx,
      }}
      {...rest}
    >
      {loading ? (
        <CircularProgress size={20} thickness={3} sx={{ color: theme.palette.background.default }} />
      ) : (
        children
      )}
    </MuiButton>
  )
}

export default AuthPrimaryButton
